import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouter, useNavigate } from "@tanstack/react-router";
import SelectDropdown, {
  TSelectOption,
} from "../../../components/forms/Select/Select";
import { Button, Typography, Brand, useToast } from "../../../components/ui";
import { useStore } from "../../../store/store";
import { Spinner } from "flowbite-react";
import {
  useGetUserDetailsQuery,
  useUpdatePrimaryRoleMutation,
} from "@/api/common/common.hooks";
import type { TUserDataSchema, TUserRoleGroup } from "@/api/user";
import { resolveLandingPage } from "@/utils/routingUtils";

/** Assigned role with internal_id used for role selection API */
type TAssignedRoleGroup = TUserRoleGroup & {
  internal_id?: string;
  permissions?: string[] | null;
};

const RoleSelect = () => {
  const { t } = useTranslation(["common"]);
  const router = useRouter();
  const navigate = useNavigate();
  const user = useStore.use.user();
  const updateAuthStore = useStore.use.updateAuthStore();
  const { toast } = useToast();
  const { data: userDetailsData, refetch: refetchUserDetails } =
    useGetUserDetailsQuery(
      {},
      {
        retry: false,
        enabled: !user || !Array.isArray(user?.role_group),
      },
    );
  const [selectedRole, setSelectedRole] = useState<TSelectOption | null>(null);

  const currentUser = useMemo((): TUserDataSchema => {
    const userDetails = userDetailsData?.data;
    if (userDetails && !Array.isArray(userDetails)) {
      return userDetails;
    }
    return user!;
  }, [user, userDetailsData?.data]);

  // set Primary Role mutation.
  const selectPrimaryRoleMutation = useUpdatePrimaryRoleMutation({
    onSuccess: async (data) => {
      if (data) {
        // Find the role data from the selected option
        const roleData = currentUser?.role_group?.find(
          (role: TUserRoleGroup) => role.slug === selectedRole?.value,
        );

        if (roleData && Array.isArray(currentUser?.role_group)) {
          toast({
            description: data?.message ?? "Role selected successfully!",
            variant: "green",
          });

          const { data: refreshedUserDetails } = await refetchUserDetails();
          const userData =
            data.data?.user ?? refreshedUserDetails?.data ?? currentUser;
          const refreshedUser = {
            ...userData,
            role_group: userData.role_group.map((role) => ({
              ...role,
              is_primary: role.slug === roleData.slug,
            })),
          };
          updateAuthStore({
            user: refreshedUser,
          });
          await router.invalidate();
          const landingPath = resolveLandingPage(refreshedUser);
          void navigate({ to: landingPath });
        }
      }
    },
    onError: (error: { message?: string }) => {
      toast({
        description: error?.message ?? "An error occurred while selecting role",
        variant: "destructive",
      });
    },
  });

  // Transform role array into dropdown options
  const roleOptions = useMemo(() => {
    return currentUser?.role_group?.map((role: TAssignedRoleGroup) => ({
      label: role.name,
      value: role.slug,
      ...role, // Include all role data for later use
    }));
  }, [currentUser]);

  const handleRoleSelect = (option: TSelectOption | null) => {
    setSelectedRole(option);
  };

  const handleContinue = () => {
    if (!selectedRole) return;

    // Find the full role object from the selected option
    const roleData = currentUser?.role_group?.find(
      (role: TUserRoleGroup) => role.slug === selectedRole.value,
    );

    if (!roleData?.internal_id) {
      toast({
        description: "Invalid role selection",
        variant: "destructive",
      });
      return;
    }

    selectPrimaryRoleMutation.mutate({
      role_internal_id: roleData.internal_id,
    });
  };

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white text-gray shadow-2xl">
        <div className="flex flex-col items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <div className="flex justify-center mb-6">
              <Brand />
            </div>

            <Typography
              variant="h3"
              className="mb-6 text-center"
              affects="bold"
            >
              {t("common:CHOOSE_YOUR_ROLE")}
            </Typography>

            <div className="space-y-6">
              <div>
                <SelectDropdown
                  name="role"
                  placeholder={t("common:SELECT_ROLE")}
                  options={roleOptions}
                  value={selectedRole}
                  onChange={handleRoleSelect}
                  getOptionLabel={(option: TSelectOption) => option.label}
                  getOptionValue={(option: TSelectOption) => option.value}
                  className="w-full"
                />
              </div>

              <Button
                type="button"
                variant="default"
                size="sm"
                className="w-full flex items-center justify-center gap-2"
                disabled={!selectedRole || selectPrimaryRoleMutation.isPending}
                onClick={handleContinue}
              >
                {t("common:CONTINUE")}
                {selectPrimaryRoleMutation.isPending && (
                  <Spinner size="sm" className="mx-1" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleSelect;
